import React from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, Sort, Filter, ExcelExport, PdfExport, FilterType } from '@syncfusion/ej2-react-grids';
import { Box, Typography, Stack } from '@mui/material';
import { WriteoffWorklistView } from '../../../../../services/WriteoffService/types/writeoffWorklistTypes';
import { SearchBar, EmptyState } from '../shared';
import { useMyApprovalsData, useSearchFilter, useMyApprovalsNavigation } from './hooks';
import {
  patientNameTemplate,
  accountNumberTemplate,
  writeoffStatusTemplate,
  serviceDateTemplate,
} from './templates';
import { gridContainerStyles } from './styles';

// Search fields for my approvals
const SEARCH_FIELDS: (keyof WriteoffWorklistView | string)[] = [
  'lastName',
  'firstName',
  'accountNumber',
  'caseId',
  'writeoffStatus',
  'payerName',
];

// Amount template
const amountTemplate = (rowData: WriteoffWorklistView) => (
  <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
    ${rowData.amount?.toFixed(2) || '0.00'}
  </Typography>
);

// Payer template
const payerTemplate = (rowData: WriteoffWorklistView) => (
  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
    {rowData.payerName || '-'}
  </Typography>
);

const MyApprovalsGrid: React.FC = () => {
  const { approvalsItems, isLoading, error, company, center } = useMyApprovalsData();
  const { handleRowSelected } = useMyApprovalsNavigation(company, center);
  const { searchTerm, setSearchTerm, clearSearch, filteredData, hasSearch } = useSearchFilter({
    data: approvalsItems,
    searchFields: SEARCH_FIELDS,
  });

  // Grid settings
  const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
  const filterSettings = { type: 'Excel' as FilterType };
  const sortSettings = { allowUnsort: false };

  if (isLoading) {
    return <EmptyState type="loading" message="Loading my approvals..." />;
  }

  if (error) {
    return <EmptyState type="error" message="Error loading my approvals. Please try again." />;
  }

  if (approvalsItems.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, writeoff ID, status..."
          />
        </Box>
        <EmptyState
          type="no-data"
          message="No approvals found"
          subMessage={`No writeoffs requiring your approval in ${center}`}
        />
      </Box>
    );
  }

  if (filteredData.length === 0 && hasSearch) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, writeoff ID, status..."
          />
        </Box>
        <EmptyState
          type="no-results"
          message="No matching results"
          subMessage={`No writeoffs found matching "${searchTerm}"`}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, writeoff ID, status..."
          />
        </Stack>
      </Box>

      {hasSearch && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredData.length} of {approvalsItems.length} approvals
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredData}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          rowSelected={handleRowSelected}
          height="400"
          enableHover={true}
          enablePersistence={false}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="accountNumber"
              headerText="Account #"
              width="120"
              template={accountNumberTemplate}
            />
            <ColumnDirective
              field="lastName"
              headerText="Patient"
              width="150"
              template={patientNameTemplate}
            />
            <ColumnDirective
              field="serviceDate"
              headerText="Service Date"
              width="110"
              template={serviceDateTemplate}
              format="yMd"
            />
            <ColumnDirective
              field="payerName"
              headerText="Payer"
              width="150"
              template={payerTemplate}
            />
            <ColumnDirective
              field="amount"
              headerText="Amount"
              width="100"
              template={amountTemplate}
              textAlign="Right"
            />
            <ColumnDirective
              field="writeoffStatus"
              headerText="Status"
              width="110"
              template={writeoffStatusTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default MyApprovalsGrid;
